import type { IGenericUser } from '@/interfaces';
import { useContainerTranslations } from '@/schoolClasses/useContainerTranslations';
import { useCurrentClass } from '@/schoolClasses/useCurrentClass';
import { shouldPresentStudentSelectionAsWholeClass } from '@/utils/students.utils';
import { computed, toValue, type ComputedRef, type MaybeRefOrGetter } from 'vue';
import { useI18n } from 'vue-i18n';

interface IStudentSelectionLabelComposable {
  label: ComputedRef<string>;
  wholeClassSelected: ComputedRef<boolean>;
}

/** Shared whole-class/group presentation; callers can keep their own names or eligibility wording as a fallback. */
export const useStudentSelectionLabel = (
  students: MaybeRefOrGetter<Pick<IGenericUser, 'id'>[]>,
  classStudents: MaybeRefOrGetter<Pick<IGenericUser, 'id'>[]>,
  fallbackLabel?: MaybeRefOrGetter<string>,
): IStudentSelectionLabelComposable => {
  const { t } = useI18n();
  const { tContainer } = useContainerTranslations();
  const { currentClass } = useCurrentClass();
  const wholeClassSelected = computed(
    () =>
      // The student endpoint filters the roster by the teacher's subject access.
      // Only call it the whole class/group when that roster is complete.
      currentClass.value?.studentCount === toValue(classStudents).length &&
      shouldPresentStudentSelectionAsWholeClass(toValue(students), toValue(classStudents)),
  );
  const label = computed(() =>
    wholeClassSelected.value
      ? tContainer('wholeSelection')
      : (toValue(fallbackLabel) ?? t('general.studentCount', { count: toValue(students).length })),
  );

  return { label, wholeClassSelected };
};
